export interface AppToolbarBreadcrumb {
  label: string;
  href?: string;
}

export interface NavigationBreadcrumbItem {
  label: string;
  path?: string;
  children?: NavigationBreadcrumbItem[];
}

export function resolveNavigationLeafPath(
  item: NavigationBreadcrumbItem | undefined,
): string {
  return item?.path ?? '';
}

/**
 * Walk the sidebar tree and return the trail of labels for the current path,
 * including parent section labels (e.g. Operations → Jobs).
 * Falls back to the longest matching nav path prefix for deep-linked routes.
 */
export function buildNavigationBreadcrumbTrail(
  items: NavigationBreadcrumbItem[],
  targetPath: string,
  trail: NavigationBreadcrumbItem[] = [],
): NavigationBreadcrumbItem[] | null {
  let best: NavigationBreadcrumbItem[] | null = null;

  for (const item of items) {
    const nextTrail = [...trail, item];
    if (item.path === targetPath) {
      return nextTrail;
    }
    if (item.path && targetPath.startsWith(`${item.path}/`)) {
      const bestPathLength = best?.[best.length - 1]?.path?.length ?? -1;
      if (item.path.length > bestPathLength) {
        best = nextTrail;
      }
    }
    if (item.children) {
      const found = buildNavigationBreadcrumbTrail(item.children, targetPath, nextTrail);
      if (found) {
        if (found[found.length - 1].path === targetPath) {
          return found;
        }
        const foundLeafPath = resolveNavigationLeafPath(found[found.length - 1]);
        const bestPathLength = best?.[best.length - 1]?.path?.length ?? -1;
        if (targetPath.startsWith(`${foundLeafPath}/`) && foundLeafPath.length > bestPathLength) {
          best = found;
        }
      }
    }
  }

  return best;
}

/** Page titles for routes not present in the sidebar navigation tree. */
const STANDALONE_ROUTE_LABELS: Record<string, string> = {
  '/operations/container-create': 'Create Container',
  '/operations/audit-workflow': 'Audit Container',
  '/profile': 'User Profile',
  '/settings': 'User Settings',
};

export function getStandaloneRouteBreadcrumb(cleanPath: string): AppToolbarBreadcrumb | null {
  const label = STANDALONE_ROUTE_LABELS[cleanPath];
  return label ? { label } : null;
}

export function navigationTrailToBreadcrumbs(
  trail: NavigationBreadcrumbItem[],
  pathPrefix: string,
): AppToolbarBreadcrumb[] {
  return trail.map((item, index) => {
    const isLast = index === trail.length - 1;
    if (!item.path || isLast) {
      return { label: item.label };
    }
    const href = pathPrefix ? `${pathPrefix}${item.path}` : item.path;
    return { label: item.label, href };
  });
}

/** Extra breadcrumb segments for dynamic/deep-linked routes not in the sidebar. */
export function getRouteBreadcrumbSuffix(cleanPath: string): AppToolbarBreadcrumb[] {
  if (cleanPath === '/operations/jobs/new') {
    return [{ label: 'New Job' }];
  }

  const jobReviewMatch = cleanPath.match(/^\/operations\/jobs\/([^/]+)\/review$/);
  if (jobReviewMatch) {
    const jobId = decodeURIComponent(jobReviewMatch[1]);
    return [
      { label: jobId, href: `/operations/jobs/${encodeURIComponent(jobId)}` },
      { label: 'Review' },
    ];
  }

  const jobDetailMatch = cleanPath.match(/^\/operations\/jobs\/([^/]+)$/);
  if (jobDetailMatch && cleanPath !== '/operations/jobs/new') {
    return [{ label: decodeURIComponent(jobDetailMatch[1]) }];
  }

  const auditRunMatch = cleanPath.match(/^\/operations\/audit-runs\/([^/]+)$/);
  if (auditRunMatch) {
    return [{ label: decodeURIComponent(auditRunMatch[1]) }];
  }

  return [];
}
